import type { Position } from '@/voxelMath'

/**
 * The screen a page world is made for (docs/pages.md, Canvas shaped to the device). The host
 * computes it when it makes a new page document (`newPageDocument`) and passes it to the page's
 * `createDocument`; a page that opts in chooses its bounds from it (`canvasFootprint`), every other
 * page ignores it. Saved worlds keep their bounds: remaking an existing world (fresh start, the
 * empty-world check) passes `keep`, the world's own bounds.
 */
export type CanvasRequest = {
  /** The viewport in CSS px when the world was made. */
  width: number
  height: number
  /** width / height. */
  aspect: number
  orientation: 'portrait' | 'landscape'
  /** A cell count per layer the host asks for instead of the page's own (unset: the page's). */
  cells?: number
  /** Remaking an existing world: its bounds, which an opted-in page keeps exactly. */
  keep?: Position
}

/** The request for the screen as it is now (undefined outside a browser: pages take their default). */
export function deviceCanvasRequest(): CanvasRequest | undefined {
  if (typeof window === 'undefined') return undefined
  const width = window.innerWidth
  const height = window.innerHeight
  if (!(width > 0 && height > 0)) return undefined
  return { width, height, aspect: width / height, orientation: width < height ? 'portrait' : 'landscape' }
}

/** The request that remakes a world with these bounds (fresh start, comparing with a page's seed). */
export function keepCanvasRequest(bounds: Position): CanvasRequest {
  const aspect = bounds.x / Math.max(1, bounds.z)
  return { width: bounds.x, height: bounds.z, aspect, orientation: aspect < 1 ? 'portrait' : 'landscape', keep: bounds }
}

export type CanvasFootprintOptions = {
  /** The page's own footprint: used without a request, and its area is the cell count kept. */
  fallback: { x: number; z: number }
  /** Cells per layer to keep (default the fallback's x * z). */
  area?: number
  /** Sides are multiples of this (default 8). */
  step?: number
  /** No side shorter than this (default 32). */
  min?: number
  /** The longest side is at most this many times the shortest (default 2.5: a tall phone still gets a sheet, not a strip). */
  maxRatio?: number
  /** px along the top and bottom edges kept for the page's bars: the sheet takes the shape between them. */
  inset?: { top?: number; bottom?: number }
}

/**
 * A flat world's footprint (x across the screen, z up it) in the screen's shape with about the same
 * cell count as the page's own: a portrait phone gets a tall sheet, landscape and desktop a wide one.
 * The opt-in one-liner for a page's `createDocument(canvas)`:
 *
 *   const { x, z } = canvasFootprint(canvas, { fallback: { x: 128, z: 96 } })
 */
export function canvasFootprint(canvas: CanvasRequest | undefined, opts: CanvasFootprintOptions): { x: number; z: number } {
  if (canvas?.keep) return { x: canvas.keep.x, z: canvas.keep.z }
  if (!canvas || !(canvas.aspect > 0)) return { ...opts.fallback }
  const step = opts.step ?? 8
  const min = opts.min ?? 32
  const maxRatio = opts.maxRatio ?? 2.5
  const area = canvas.cells ?? opts.area ?? opts.fallback.x * opts.fallback.z
  const free = Math.max(canvas.height * 0.4, canvas.height - (opts.inset?.top ?? 0) - (opts.inset?.bottom ?? 0))
  const aspect = Math.min(maxRatio, Math.max(1 / maxRatio, canvas.width / free))
  const round = (v: number) => Math.max(min, Math.round(v / step) * step)
  return { x: round(Math.sqrt(area * aspect)), z: round(Math.sqrt(area / aspect)) }
}
